import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { X, ArrowRight, PlusCircle, UploadSimple, Trophy, Lightning } from "@phosphor-icons/react";

/**
 * 3-step onboarding tour shown on Dashboard the first time a user lands here.
 * Especially important for Google-signup users who go straight from OAuth to
 * an empty dashboard and don't know what to do.
 *
 * Uses localStorage `tj_onboarding_v1` so it never nags returning users.
 * The parent Dashboard passes callbacks that open the real Add-Trade / CSV
 * dialogs so a tap on the CTA jumps straight into the flow.
 */

const KEY = "tj_onboarding_v1";

export function shouldShowOnboarding() {
  try { return !localStorage.getItem(KEY); } catch { return false; }
}
export function markOnboardingSeen() {
  try { localStorage.setItem(KEY, "1"); } catch { /* noop */ }
}

export default function OnboardingTour({ open, userName, onClose, onOpenAddTrade, onOpenCsv, onGoPropChallenge }) {
  const steps = [
    {
      icon: PlusCircle,
      accent: "from-blue-500/25 to-blue-500/5",
      badge: "Step 1 of 3",
      title: "Log your first trade",
      body: "Tradelog turns your raw trades into a P&L calendar, discipline score and equity curve. Start by dropping in one trade — you can add screenshots and notes too.",
      cta: "Add my first trade",
      action: onOpenAddTrade,
    },
    {
      icon: UploadSimple,
      accent: "from-emerald-500/25 to-emerald-500/5",
      badge: "Step 2 of 3",
      title: "Or import your broker CSV",
      body: "Already have hundreds of trades in MT4, MT5, Zerodha, Exness, Binance or TradingView? Drop the CSV — we auto-map every column and build your journal in seconds.",
      cta: "Import a CSV",
      action: onOpenCsv,
    },
    {
      icon: Trophy,
      accent: "from-amber-500/25 to-amber-500/5",
      badge: "Step 3 of 3",
      title: "Take the Prop Firm Challenge",
      body: "Trade real market symbols with virtual money under real prop-firm rules — profit target, daily loss, drawdown, no news, no overnight. Pass, get a shareable certificate.",
      cta: "Start the Challenge",
      action: onGoPropChallenge,
    },
  ];
  const [i, setI] = useState(0);
  const step = steps[i];
  const last = i === steps.length - 1;

  useEffect(() => { if (open) setI(0); }, [open]);
  if (!open) return null;

  const finish = () => { markOnboardingSeen(); onClose?.(); };
  const skip   = () => { markOnboardingSeen(); onClose?.(); };
  const doAction = () => {
    markOnboardingSeen();
    onClose?.();
    step.action?.();
  };

  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="onboarding-tour">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card overflow-hidden shadow-2xl">
        <div className={`relative p-8 bg-gradient-to-br ${step.accent}`}>
          <button
            onClick={skip}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            data-testid="onboarding-close">
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={14} weight="fill" className="text-primary" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-semibold">
              Tradelog · Welcome{userName ? `, ${userName.split(" ")[0]}` : ""} · {step.badge}
            </p>
          </div>
          <div className="w-14 h-14 rounded-xl bg-white/8 border border-white/10 grid place-items-center mb-4">
            <Icon size={28} weight="fill" className="text-primary" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="onboarding-title">
            {step.title}
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="flex gap-1.5">
            {steps.map((_, k) => (
              <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "bg-primary w-6" : "bg-muted-foreground/40 w-1.5"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={skip}
              className="rounded-full text-xs"
              data-testid="onboarding-skip">
              Skip tour
            </Button>
            <Button
              onClick={doAction}
              className="rounded-full"
              data-testid="onboarding-cta">
              {step.cta}
              <ArrowRight size={14} className="ml-1" />
            </Button>
            {!last && (
              <Button
                variant="outline"
                onClick={() => setI(i + 1)}
                className="rounded-full text-xs"
                data-testid="onboarding-next">
                Next
              </Button>
            )}
            {last && (
              <Button
                variant="outline"
                onClick={finish}
                className="rounded-full text-xs"
                data-testid="onboarding-done">
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
